// src/app/pages/admin/MarcacionVacanteCandidatoTable.tsx
import { useEffect, useState } from 'react';
import styleScss from '../../pages/admin/styles.module.scss';
import {
	getVacantesCandidatoEvento,
	guardarMarcacionRemisionPotencial,
} from '../../modules/apps/user-management/users-list/core/_requests';
import { LoadingBackdrop } from '../../components/LoadingSpinner/LoadingSpinner';
import {
	Button,
	Checkbox,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
} from '@mui/material';

import { CandidatoBusqueda, getErrorMessage } from '../../pages/admin/EventosEmpleabilidad.helpers';

const headerCellStyle = { fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' as const };
const bodyCellStyle = { fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' as const };


interface VacanteCandidato {
	id: number; // id de la vacante
	codigo_vacante?: string ;
	nombre_vacante?: string ;
	razon_social?: string;
	marcado: boolean; // viene del backend según si ya existe marcación
}

interface MarcacionVacanteCandidatoTableProps {
	eventoId: string;
	candidato: CandidatoBusqueda;
	embedded?: boolean;
	readOnly?: boolean;
	onGuardado: () => void;
	onError: (msg: string) => void;
	onSuccess: (msg: string) => void;
}

const MarcacionVacanteCandidatoTable = ({
	eventoId,
	candidato,
	embedded = false,
	readOnly = false,
	onGuardado,
	onError,
	onSuccess,
}: MarcacionVacanteCandidatoTableProps) => {
	const [vacantes, setVacantes] = useState<VacanteCandidato[]>([]);
	const [filtro, setFiltro] = useState('');
	const [filtroAplicado, setFiltroAplicado] = useState('');
	const [loading, setLoading] = useState(false);
	const [guardando, setGuardando] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [marcadas, setMarcadas] = useState<Set<number>>(new Set());

	const cargar = () => {
		setLoading(true);
		getVacantesCandidatoEvento(eventoId, candidato.id)
			.then((vacantesResp) => {
			console.log('respuesta de data', vacantesResp);
			setVacantes(vacantesResp);
			setMarcadas(new Set(vacantesResp.filter((v) => v.marcado).map((v) => v.id)));
			setPage(0);
			})
			.catch((error: any) => onError(getErrorMessage(error)))
			.finally(() => setLoading(false));
		};

	useEffect(() => {
		cargar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [candidato.id]);

	const vacantesFiltradas = vacantes.filter(
		(v) =>
			v.codigo_vacante?.toLowerCase().includes(filtroAplicado.toLowerCase()) ||
			v.nombre_vacante?.toLowerCase().includes(filtroAplicado.toLowerCase())
	);

	const handleVisualizarVacantes = () => {
		setFiltroAplicado(filtro.trim());
		setPage(0);
	};

	const toggleMarcada = (vacanteId: number) => {
		setMarcadas((prev) => {
			const next = new Set(prev);
			next.has(vacanteId) ? next.delete(vacanteId) : next.add(vacanteId);
			return next;
		});
	};

	const handleConfirmarMarcacion = () => {
		setGuardando(true);
		guardarMarcacionRemisionPotencial(eventoId, candidato.id, Array.from(marcadas))
			.then(() => {
				onSuccess('Marcación guardada con éxito.');
				onGuardado();
			})
			.catch((error: any) => onError(getErrorMessage(error)))
			.finally(() => setGuardando(false));
	};

	return (
		<div className={embedded ? '' : 'p-4 shadow-4 rounded-3 mt-15'} style={embedded ? undefined : { backgroundColor: '#FFFF' }}>
			{!embedded && <LoadingBackdrop loading={loading || guardando} />}
			<h3 className='mb-4'>Detalle de vacantes asociadas a {candidato.nombre_completo}</h3>

			<div className='input-group d-flex flex-wrap align-items-center' style={{ gap: '10px' }}>
				<TextField
					size='small'
					label='Código vacante / Nombre vacante'
					value={filtro}
					onChange={(e) => setFiltro(e.target.value)}
				/>
				<Button variant='contained' onClick={handleVisualizarVacantes}>
					Visualizar vacantes de empresas
				</Button>
				<Button
					variant='outlined'
					disabled={!filtro}
					onClick={() => {
						setFiltro('');
						setFiltroAplicado('');
						setPage(0);
					}}
				>
					Limpiar filtros
				</Button>
			</div>

			<div className={`${styleScss['table-limit']}`} style={{ maxWidth: '100%', overflowWrap: 'break-word', overflowY: 'hidden' }}>
				<Stack padding={2}>
					<TableContainer component={Paper} style={{ overflowY: 'visible', overflowWrap: 'break-word' }}>
						<Table style={{ overflowY: 'visible' }}>
							<TableHead className={`${styleScss['th-custom']}`}>
								<TableRow>
									<TableCell style={headerCellStyle}><h4>Código vacante</h4></TableCell>
									<TableCell style={headerCellStyle}><h4>Nombre vacante</h4></TableCell>
									<TableCell style={headerCellStyle}><h4>Razón social</h4></TableCell>
									<TableCell style={headerCellStyle}><h4>Marcación remisión potencial</h4></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{vacantesFiltradas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((v) => (
									<TableRow key={v.id}>
										<TableCell style={bodyCellStyle}>{v.codigo_vacante}</TableCell>
										<TableCell style={bodyCellStyle}>{v.nombre_vacante}</TableCell>
										<TableCell style={bodyCellStyle}>{v.razon_social}</TableCell>
										<TableCell style={bodyCellStyle}>
											<Checkbox
												checked={marcadas.has(v.id)}
												disabled={readOnly}
												onChange={() => toggleMarcada(v.id)}
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
							{!loading && vacantesFiltradas.length === 0 && (
								<tfoot>
									<TableRow>
										<TableCell colSpan={4}>
											<p className='text-center' style={{ width: '100%', textAlign: 'center' }}>
												No se encontraron registros
											</p>
										</TableCell>
									</TableRow>
								</tfoot>
							)}
						</Table>
						<TablePagination
							rowsPerPageOptions={[10, 20, 50]}
							component='div'
							style={{ overflow: 'visible', fontFamily: 'Poppins', fontSize: '14.5px' }}
							count={vacantesFiltradas.length}
							rowsPerPage={rowsPerPage}
							page={page}
							labelRowsPerPage='Filas por página'
							onPageChange={(_e, newPage) => setPage(newPage)}
							onRowsPerPageChange={(e) => {
								setRowsPerPage(parseInt(e.target.value, 10));
								setPage(0);
							}}
						/>
					</TableContainer>
				</Stack>
			</div>

			<Stack direction='row' justifyContent='flex-end' sx={{ mt: 2 }}>
				<Button variant='contained' disabled={readOnly || guardando || loading} onClick={handleConfirmarMarcacion}>
					Confirmar marcación
				</Button>
			</Stack>
		</div>
	);
};

export default MarcacionVacanteCandidatoTable;